import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { TableHeadNew } from '@/components-new/TableNew/TableHeadNew';
import { TableHeadCellNew } from '@/components-new/TableNew/TableHeadCellNew';
import { TableBodyNew, TableCellNew, TableRowNew } from '@/components-new/TableNew/styles';
import { formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { DashboardSummaryType, TaskScopeType, TaskType, TaskViewType } from '@/types/dashboardTypes';
import { TASK_VIEWS } from '../const/const';
import { UnreadDot } from '../styles';
import { TableNew } from '@/components-new/TableNew/TableNew';
import { Pills } from '@/components/Pills/Pills';
import { Pill } from '@/components/Pills/styles';
import { TasksStatus } from './TasksStatus';

type TasksTableType = {
  tasks: TaskType[];
  isLoading: boolean;
  scope: TaskScopeType;
  view: TaskViewType;
  counts?: DashboardSummaryType['tasks'];
  onScope: (scope: TaskScopeType) => void;
  onView: (view: TaskViewType) => void;
  onOpen: (id: string) => void;
};

export const TasksTable = ({ tasks = [], isLoading, scope, view, counts, onScope, onView, onOpen }: TasksTableType) => {
  const hasItems = tasks.length > 0;

  return (
    <TableNew
      header={
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box display='flex' flexDirection='column' gap='10px'>
            <Pills>
              <Pill $active={scope === 'assigned_by_me'} onClick={() => onScope('assigned_by_me')}>
                Tasks you assigned
              </Pill>
              <Pill $active={scope === 'assigned_to_me'} onClick={() => onScope('assigned_to_me')}>
                Assigned to me{counts?.assigned_to_me_open ? ` (${counts.assigned_to_me_open})` : ''}
              </Pill>
            </Pills>
            <Typography variant='caption'>
              {scope === 'assigned_by_me'
                ? 'Next steps handed to the team. You are notified when they finish or ask something.'
                : 'Next steps handed to you. Start them, ask a question or mark them completed.'}
            </Typography>
          </Box>
          <Pills>
            {TASK_VIEWS.map((item) => (
              <Pill key={item.key} $active={view === item.key} onClick={() => onView(item.key)}>
                {item.label}
              </Pill>
            ))}
          </Pills>
        </Box>
      }
      itemsCount={tasks.length}
      isLoading={isLoading}
      noItems='No tasks in this view.'
    >
      {hasItems && (
        <>
          <TableHeadNew>
            <TableHeadCellNew>TASK</TableHeadCellNew>
            <TableHeadCellNew>{scope === 'assigned_to_me' ? 'FROM' : 'ASSIGNED TO'}</TableHeadCellNew>
            <TableHeadCellNew>LINKED TO</TableHeadCellNew>
            <TableHeadCellNew>DUE</TableHeadCellNew>
            <TableHeadCellNew>STATUS</TableHeadCellNew>
            <TableHeadCellNew></TableHeadCellNew>
          </TableHeadNew>
          <TableBodyNew>
            {tasks.map((task) => (
              <TableRowNew key={task.id}>
                <TableCellNew>
                  <Box display='flex' flexDirection='column' gap='2px'>
                    <Typography variant='bodySmall' fontWeight='medium'>
                      {task.title}
                    </Typography>
                    {task.last_message && (
                      <Typography variant='bodySmall2'>
                        {task.last_message.author_label}: {task.last_message.text}
                      </Typography>
                    )}
                  </Box>
                </TableCellNew>
                <TableCellNew>
                  {scope === 'assigned_to_me' ? task.created_by_label : task.assigned_to_label}
                </TableCellNew>
                <TableCellNew>
                  <Typography variant='bodySmall2'>{task.linked_label || '-'}</Typography>
                </TableCellNew>
                <TableCellNew>
                  <Typography variant='bodySmall2'>{task.due_date ? formatDate(task.due_date) : 'Not set'}</Typography>
                </TableCellNew>
                <TableCellNew>
                  <Box display='flex' gap='6px' alignItems='center'>
                    <TasksStatus status={task.status}>{task.status_label}</TasksStatus>
                    {task.unread && <UnreadDot aria-label='Unread' />}
                  </Box>
                </TableCellNew>
                <TableCellNew>
                  <ButtonNew size='medium' variant='secondary' onClick={() => onOpen(task.id)}>
                    {task.cta}
                  </ButtonNew>
                </TableCellNew>
              </TableRowNew>
            ))}
          </TableBodyNew>
        </>
      )}
    </TableNew>
  );
};
